import React from "react";
import { Link } from "react-router-dom";
import Card from "components/card";
import DteEmissionStatsChart from "./components/DteEmissionStatsChart";
import DashboardDateRangeFloat from "./components/DashboardDateRangeFloat";
import HourlySalesHeatmap from "./components/HourlySalesHeatmap";
import TopProductsTable from "./components/TopProductsTable";
import { buildTopProducts } from "./components/topProductsMetrics";
import { useAuth } from "context/AuthContext";
import { APP_NAME } from "config/branding";
import { useShallow } from "zustand/react/shallow";
import {
  cafStockApi,
  dteDocumentsApi,
  emissionProfilesApi,
  gmailDteInboxApi,
  organizationsApi,
  foliosApi,
} from "api/endpoints";
import { useEmissionAmbienteStore } from "stores/emissionAmbienteStore";
import { labelTipoDte } from "utils/cafXmlPreview";
import { labelAmbienteSii } from "utils/siiAmbiente";
import {
  MdBarChart,
  MdDescription,
  MdInventory2,
  MdMoneyOff,
  MdOpenInNew,
  MdOutlineEmail,
  MdOutlinePointOfSale,
  MdOutlineReceiptLong,
  MdOutlineWarningAmber,
  MdRefresh,
  MdSettings,
  MdTrendingUp,
  MdUpload,
} from "react-icons/md";

// Secciones del dashboard, en el orden en que aparecen. Alimenta el índice de
// arriba; cada clave corresponde a un id="seccion-..." más abajo.
// Las etiquetas van en español fijo, como el resto de esta pantalla: el
// dashboard todavía no usa el diccionario de traducciones y traducir solo el
// índice lo dejaría a medias.
const SECCIONES_DASHBOARD = [
  { id: "ventas", etiqueta: "Ventas" },
  { id: "folios", etiqueta: "Folios" },
  { id: "compras", etiqueta: "Compras" },
  { id: "ultimos", etiqueta: "Últimos DTE" },
  { id: "rechazos", etiqueta: "Rechazos" },
  { id: "accesos", etiqueta: "Accesos rápidos" },
];

function formatInt(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("es-CL").format(n);
}

function formatDateShort(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n);
}

function isoTodayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function isoMonthStartLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function isReparoLikeValue(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return false;
  return normalized.includes("reparo") || normalized.includes("rpr");
}

function isCriticalDteRow(row = {}) {
  const lifecycle = String(row.lifecycle_status || "").trim().toLowerCase();
  if (lifecycle === "accepted") return false;
  if (lifecycle === "rejected" || lifecycle === "error") return true;
  return isReparoLikeValue(row.sii_estado) || isReparoLikeValue(row.sii_glosa);
}

function buildCriticalDteFilterLink(row = {}) {
  const lifecycle = String(row.lifecycle_status || "").trim().toLowerCase();
  const params = new URLSearchParams();
  if (lifecycle === "rejected" || lifecycle === "error") {
    params.set("lifecycle_status", lifecycle);
  } else {
    params.set("q", "reparo");
  }
  return `/admin/data-tables?${params.toString()}`;
}

function mergeCriticalRows(...groups) {
  const map = new Map();
  for (const group of groups) {
    for (const row of Array.isArray(group) ? group : []) {
      const key = row?.id != null ? `id:${row.id}` : `uuid:${row?.document_uuid || Math.random()}`;
      if (!map.has(key)) {
        map.set(key, row);
      }
    }
  }
  return Array.from(map.values())
    .filter(isCriticalDteRow)
    .sort((a, b) => {
      const ta = new Date(a?.created_at || 0).getTime();
      const tb = new Date(b?.created_at || 0).getTime();
      return tb - ta;
    });
}

/** Por debajo de este número de folios disponibles → alerta (warning). “OK” si n ≥ umbral. */
const FOLIOS_ALERTA_UMBRAL = 5;

function folioPieStock(n) {
  const x = Number(n ?? 0);
  if (x <= 0) return "Sin folios en este rango. Subí un CAF nuevo.";
  if (x === 1)
    return "Subí CAF pronto: queda 1 folio en este rango.";
  return `Subí CAF pronto: quedan ${formatInt(x)} folios en este rango.`;
}

const SII_PORTAL = "https://www4.sii.cl/siife/cierres/";

export default function DteCrmDashboard() {
  const { user } = useAuth();
  const emission = useEmissionAmbienteStore(
    useShallow((s) => ({
      kind: s.kind,
      ambiente: s.ambiente,
      profileUuid: s.profileUuid,
      rutEmisor: s.rutEmisor,
    })),
  );
  const displayName = user?.full_name || user?.email || "Usuario";
  const selectedAmbiente =
    emission.kind === "ready" && emission.ambiente ? emission.ambiente : "";
  const selectedProfileUuid =
    emission.kind === "ready" && emission.profileUuid ? String(emission.profileUuid).trim() : "";
  const selectedRutEmisor =
    emission.kind === "ready" && emission.rutEmisor ? String(emission.rutEmisor).trim() : "";
  // Proveedores a los que más se compró en el rango del filtro general.
  const [topProveedores, setTopProveedores] = React.useState([]);
  const [dashboardDesde, setDashboardDesde] = React.useState(() => isoMonthStartLocal());
  const [dashboardHasta, setDashboardHasta] = React.useState(() => isoTodayLocal());
  const [commercialSummary, setCommercialSummary] = React.useState(null);

  const [kpi, setKpi] = React.useState({
    loading: true,
    dteTotal: null,
    orgs: null,
    profiles: null,
    caf: null,
  });
  const [inboxPreview, setInboxPreview] = React.useState({
    loading: true,
    items: [],
  });
  const [recentDte, setRecentDte] = React.useState({
    loading: true,
    items: [],
  });
  const [criticalDte, setCriticalDte] = React.useState({
    loading: true,
    items: [],
    summary: {
      rejected: 0,
      error: 0,
      reparo: 0,
    },
  });
  const [folioRows, setFolioRows] = React.useState({
    loading: true,
    items: [],
  });
  const [topProducts, setTopProducts] = React.useState({
    loading: true,
    items: [],
  });
  const [topProductsSortBy, setTopProductsSortBy] = React.useState("units");

  const folioDisponiblesItems = React.useMemo(
    () =>
      folioRows.items.filter(
        (r) =>
          r.estado_actual === "available" ||
          r.estado_actual === "pending_activation",
      ),
    [folioRows.items],
  );
  const criticalTotal = React.useMemo(
    () =>
      Number(criticalDte.summary.rejected || 0)
      + Number(criticalDte.summary.error || 0)
      + Number(criticalDte.summary.reparo || 0),
    [criticalDte.summary],
  );

  const refreshFolios = React.useCallback(async () => {
    if (!selectedAmbiente) {
      setFolioRows({ loading: false, items: [] });
      return;
    }
    setFolioRows((s) => ({ ...s, loading: true }));
    try {
      const qs = new URLSearchParams();
      if (selectedAmbiente) qs.set("ambiente", selectedAmbiente);
      if (selectedProfileUuid) qs.set("profileUuid", selectedProfileUuid);
      if (selectedRutEmisor) qs.set("rut_emisor", selectedRutEmisor);
      const res = await foliosApi.listSequences(qs.toString() ? `?${qs.toString()}` : "");
      if (!res?.res?.ok) {
        setFolioRows({ loading: false, items: [] });
        return;
      }
      const raw = res.data?.items || [];
      const sorted = [...raw].sort(
        (a, b) =>
          Number(a.folios_disponibles ?? 0) -
          Number(b.folios_disponibles ?? 0),
      );
      setFolioRows({ loading: false, items: sorted });
    } catch {
      setFolioRows({ loading: false, items: [] });
    }
  }, [selectedAmbiente, selectedProfileUuid, selectedRutEmisor, dashboardDesde, dashboardHasta]);

  React.useEffect(() => {
    if (!selectedAmbiente) {
      setKpi({
        loading: false,
        dteTotal: 0,
        orgs: 0,
        profiles: 0,
        caf: 0,
      });
      setRecentDte({ loading: false, items: [] });
      setCriticalDte({
        loading: false,
        items: [],
        summary: { rejected: 0, error: 0, reparo: 0 },
      });
      setInboxPreview({ loading: false, items: [] });
      setFolioRows({ loading: false, items: [] });
      setTopProducts({ loading: false, items: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      const baseQs = new URLSearchParams();
      if (selectedAmbiente) {
        baseQs.set("modo", selectedAmbiente);
      }
      baseQs.set("fch_emis_desde", dashboardDesde);
      baseQs.set("fch_emis_hasta", dashboardHasta);
      if (selectedProfileUuid) {
        baseQs.set("profileUuid", selectedProfileUuid);
      }
      if (selectedRutEmisor) {
        baseQs.set("rut_emisor", selectedRutEmisor);
      }
      const cafQs = new URLSearchParams();
      if (selectedAmbiente) {
        cafQs.set("ambiente", selectedAmbiente);
      }
      if (selectedProfileUuid) {
        cafQs.set("profileUuid", selectedProfileUuid);
      }
      if (selectedRutEmisor) {
        cafQs.set("rut_emisor", selectedRutEmisor);
      }
      const folioQs = new URLSearchParams();
      if (selectedAmbiente) {
        folioQs.set("ambiente", selectedAmbiente);
      }
      if (selectedProfileUuid) {
        folioQs.set("profileUuid", selectedProfileUuid);
      }
      if (selectedRutEmisor) {
        folioQs.set("rut_emisor", selectedRutEmisor);
      }
      const settled = await Promise.allSettled([
        dteDocumentsApi.list(
          `?${new URLSearchParams({
            ...Object.fromEntries(baseQs.entries()),
            limit: "1",
            offset: "0",
          }).toString()}`,
        ),
        organizationsApi.list(),
        emissionProfilesApi.list({
          ambiente: selectedAmbiente,
          ...(selectedRutEmisor ? { rut_emisor: selectedRutEmisor } : {}),
          ...(selectedProfileUuid ? { profile_uuid: selectedProfileUuid } : {}),
        }),
        cafStockApi.list(cafQs.toString() ? `?${cafQs.toString()}` : ""),
        dteDocumentsApi.list(
          `?${new URLSearchParams({
            ...Object.fromEntries(baseQs.entries()),
            limit: "6",
            offset: "0",
            sort_by: "created_at",
            sort_dir: "desc",
          }).toString()}`,
        ),
        gmailDteInboxApi.list({ limit: 5 }),
        foliosApi.listSequences(folioQs.toString() ? `?${folioQs.toString()}` : ""),
        dteDocumentsApi.list(
          `?${new URLSearchParams({
            ...Object.fromEntries(baseQs.entries()),
            lifecycle_status: "rejected",
            limit: "50",
            offset: "0",
            sort_by: "created_at",
            sort_dir: "desc",
          }).toString()}`,
        ),
        dteDocumentsApi.list(
          `?${new URLSearchParams({
            ...Object.fromEntries(baseQs.entries()),
            lifecycle_status: "error",
            limit: "50",
            offset: "0",
            sort_by: "created_at",
            sort_dir: "desc",
          }).toString()}`,
        ),
        dteDocumentsApi.list(
          `?${new URLSearchParams({
            ...Object.fromEntries(baseQs.entries()),
            q: "reparo",
            limit: "50",
            offset: "0",
            sort_by: "created_at",
            sort_dir: "desc",
          }).toString()}`,
        ),
        dteDocumentsApi.list(
          `?${new URLSearchParams({
            ...Object.fromEntries(baseQs.entries()),
            lifecycle_status: "accepted",
            limit: "500",
            page: "1",
            sort_by: "fch_emis",
            sort_dir: "desc",
          }).toString()}`,
        ),
        // Va AL FINAL a propósito: los resultados de abajo se leen por índice
        // (settled[0], settled[1]...), así que insertar en medio correría todos
        // los siguientes y cada sección mostraría los datos de otra.
        //
        // Mismo rango que el filtro general: si se cambia el mes, el ranking
        // cambia con él.
        gmailDteInboxApi.topProveedores({
          received_desde: dashboardDesde,
          received_hasta: dashboardHasta,
          limit: 8,
        }),
      ]);
      if (cancelled) return;

      const d = {};
      if (settled[0].status === "fulfilled" && settled[0].value?.res?.ok) {
        d.dteTotal = settled[0].value.data?.total;
      }
      if (settled[1].status === "fulfilled" && settled[1].value?.res?.ok) {
        d.orgs = settled[1].value.data?.items?.length;
      }
      if (settled[2].status === "fulfilled" && settled[2].value?.res?.ok) {
        d.profiles = settled[2].value.data?.items?.length;
      }
      if (settled[3].status === "fulfilled" && settled[3].value?.res?.ok) {
        d.caf = settled[3].value.data?.items?.length;
      }
      setKpi({ loading: false, ...d });

      // El último del arreglo, sin número fijo: si mañana se agrega otra
      // llamada al final, esto sigue apuntando a la correcta.
      const resTop = settled[settled.length - 1];
      setTopProveedores(
        resTop?.status === "fulfilled" && resTop.value?.res?.ok
          ? (resTop.value.data?.items || [])
          : [],
      );

      if (settled[4].status === "fulfilled" && settled[4].value?.res?.ok) {
        setRecentDte({
          loading: false,
          items: settled[4].value.data?.items || [],
        });
      } else {
        setRecentDte({ loading: false, items: [] });
      }

      if (settled[5].status === "fulfilled" && settled[5].value?.res?.ok) {
        setInboxPreview({
          loading: false,
          items: settled[5].value.data?.items || [],
        });
      } else {
        setInboxPreview({ loading: false, items: [] });
      }

      if (settled[6].status === "fulfilled" && settled[6].value?.res?.ok) {
        const raw = settled[6].value.data?.items || [];
        const sorted = [...raw].sort(
          (a, b) =>
            Number(a.folios_disponibles ?? 0) -
            Number(b.folios_disponibles ?? 0),
        );
        setFolioRows({ loading: false, items: sorted });
      } else {
        setFolioRows({ loading: false, items: [] });
      }

      const rejectedRows =
        settled[7].status === "fulfilled" && settled[7].value?.res?.ok
          ? settled[7].value.data?.items || []
          : [];
      const rejectedTotal =
        settled[7].status === "fulfilled" && settled[7].value?.res?.ok
          ? Number(settled[7].value.data?.total ?? rejectedRows.length)
          : 0;
      const errorRows =
        settled[8].status === "fulfilled" && settled[8].value?.res?.ok
          ? settled[8].value.data?.items || []
          : [];
      const errorTotal =
        settled[8].status === "fulfilled" && settled[8].value?.res?.ok
          ? Number(settled[8].value.data?.total ?? errorRows.length)
          : 0;
      const reparoRows =
        settled[9].status === "fulfilled" && settled[9].value?.res?.ok
          ? settled[9].value.data?.items || []
          : [];
      const reparoTotal =
        settled[9].status === "fulfilled" && settled[9].value?.res?.ok
          ? Number(settled[9].value.data?.total ?? reparoRows.length)
          : 0;
      const mergedCritical = mergeCriticalRows(rejectedRows, errorRows, reparoRows).slice(0, 3);
      setCriticalDte({
        loading: false,
        items: mergedCritical,
        summary: {
          rejected: rejectedTotal,
          error: errorTotal,
          reparo: reparoTotal,
        },
      });

      if (settled[10].status === "fulfilled" && settled[10].value?.res?.ok) {
        setTopProducts({
          loading: false,
          items: buildTopProducts(settled[10].value.data?.items || []),
        });
      } else {
        setTopProducts({ loading: false, items: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedAmbiente, selectedProfileUuid, selectedRutEmisor, dashboardDesde, dashboardHasta]);

  const commercialCards = React.useMemo(
    () => [
      {
        key: "net-sales",
        icon: <MdTrendingUp className="w-6 h-6" />,
        title: "Ventas netas",
        value: formatMoney(commercialSummary?.netSalesTotal ?? 0),
        hint: "Aceptadas: facturas + boletas - NC",
        accent:
          "from-emerald-500/15 to-emerald-100/60 dark:from-emerald-500/15 dark:to-emerald-500/5",
      },
      {
        key: "avg-ticket",
        icon: <MdOutlinePointOfSale className="w-6 h-6" />,
        title: "Ticket promedio",
        value: formatMoney(commercialSummary?.avgTicket ?? 0),
        hint: `${formatInt(commercialSummary?.salesCount ?? 0)} órdenes · neto de NC`,
        accent:
          "from-sky-500/15 to-sky-100/60 dark:from-sky-500/15 dark:to-sky-500/5",
      },
      {
        key: "projection",
        icon: <MdBarChart className="w-6 h-6" />,
        title: "Proyección cierre",
        value: formatMoney(commercialSummary?.projectedMonthNetSales ?? 0),
        hint: `Promedio diario: ${formatMoney(commercialSummary?.avgDailyNetSales ?? 0)}`,
        accent:
          "from-violet-500/15 to-violet-100/60 dark:from-violet-500/15 dark:to-violet-500/5",
      },
      {
        key: "total-orders",
        icon: <MdOutlineReceiptLong className="w-6 h-6" />,
        title: "Total de órdenes",
        value: formatInt(commercialSummary?.salesCount ?? 0),
        hint: `Facturas ${formatInt(commercialSummary?.facturaCount ?? 0)} · Boletas ${formatInt(
          commercialSummary?.boletaCount ?? 0,
        )} aceptadas`,
        accent:
          "from-amber-500/15 to-amber-100/60 dark:from-amber-500/15 dark:to-amber-500/5",
      },
      {
        key: "units-per-order",
        icon: <MdInventory2 className="w-6 h-6" />,
        title: "Unidades por pedido",
        value: new Intl.NumberFormat("es-CL", {
          maximumFractionDigits: 2,
        }).format(Number(commercialSummary?.unitsPerOrder ?? 0)),
        hint: `${formatInt(commercialSummary?.totalUnits ?? 0)} unidades en ${formatInt(
          commercialSummary?.salesCount ?? 0,
        )} pedidos`,
        accent:
          "from-rose-500/15 to-rose-100/60 dark:from-rose-500/15 dark:to-rose-500/5",
      },
      {
        key: "credit-notes",
        icon: <MdMoneyOff className="w-6 h-6" />,
        title: "Notas de crédito descontadas",
        value: formatMoney(commercialSummary?.creditNoteAmount ?? 0),
        hint: `${formatInt(commercialSummary?.creditNoteCount ?? 0)} notas de crédito aceptadas`,
        accent:
          "from-red-500/15 to-red-100/60 dark:from-red-500/15 dark:to-red-500/5",
      },
    ],
    [commercialSummary],
  );

  return (
    <div className="px-3 pb-10 mx-auto w-full max-w-7xl text-navy-700 dark:text-white sm:px-4 lg:px-6">
      <div className="p-4 mt-3 rounded-2xl border shadow-sm border-gray-200/80 bg-white/95 dark:border-white/10 dark:bg-navy-900/70 sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
              Hola, {displayName}
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Centro operativo de {APP_NAME}: monitoreo de emisión, incidentes y bandejas.
            </p>
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
              <span className="px-2.5 py-1 font-medium text-gray-700 bg-gray-50 rounded-full border border-gray-200 dark:border-white/15 dark:bg-white/5 dark:text-gray-200">
                Ambiente: {labelAmbienteSii(selectedAmbiente) || "—"}
              </span>
              <span className="px-2.5 py-1 font-medium text-gray-700 bg-gray-50 rounded-full border border-gray-200 dark:border-white/15 dark:bg-white/5 dark:text-gray-200">
                RUT emisor: {selectedRutEmisor || "—"}
              </span>
              <span className="px-2.5 py-1 font-semibold text-amber-800 bg-amber-50 rounded-full border border-amber-200 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                Incidentes activos: {formatInt(criticalTotal)}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Link
              to="/admin/data-tables"
              className="px-3 py-2 text-sm font-semibold text-white rounded-lg transition bg-brand-500 hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-300"
            >
              Ver DTE emitidos
            </Link>
            <Link
              to="/admin/dte-compras"
              className="px-3 py-2 text-sm font-semibold bg-white rounded-lg border border-gray-300 transition text-navy-700 hover:bg-gray-50 dark:border-white/15 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
            >
              Ir a compras
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-5 lg:fixed lg:bottom-6 lg:left-1/2 lg:z-40 lg:w-[min(calc(100vw-2rem),80rem)] lg:-translate-x-1/2">
        <DashboardDateRangeFloat
          desde={dashboardDesde}
          hasta={dashboardHasta}
          onDesdeChange={setDashboardDesde}
          onHastaChange={setDashboardHasta}
        />

      </div>
      {/* Índice de secciones. El dashboard creció y había que bajar scrolleando
          a ciegas para llegar a cualquier cosa. Son enlaces a anclas: no cambian
          de página, no recargan datos y funcionan con el botón atrás. */}
      <nav
        aria-label="Secciones del dashboard"
        className="mt-5 flex flex-wrap gap-2 rounded-[20px] border border-gray-200/70 bg-white p-3 dark:border-white/10 dark:bg-navy-800"
      >
        {SECCIONES_DASHBOARD.map((s) => (
          <a
            key={s.id}
            href={`#seccion-${s.id}`}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-100 hover:text-navy-700 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
          >
            {s.etiqueta}
          </a>
        ))}
      </nav>

      <Card id="seccion-ventas" extra="mt-5 rounded-[20px] border border-gray-200/70 p-4 dark:border-white/10 sm:p-5">
        <div>
          <h3 className="text-sm font-bold text-navy-700 dark:text-white">
            Ventas y proyección
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Métricas comerciales del rango activo: ventas netas, ticket promedio, proyección,
            unidades por pedido y notas de crédito descontadas.
          </p>
        </div>

        <div className="overflow-x-auto px-4 pb-1 -mx-4 mt-4 sm:mx-0 sm:px-0">
          <div className="flex gap-3 snap-x snap-mandatory sm:grid sm:grid-cols-2 sm:gap-4 xl:grid-cols-4">
            {commercialCards.map((card) => (
              <div
                key={card.key}
                className={`min-w-[17rem] snap-start rounded-[20px] border border-gray-200/80 bg-gradient-to-br ${card.accent} p-4 shadow-sm dark:border-white/10 sm:min-w-0`}
              >
                <div className="flex gap-3 justify-between items-start">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {card.title}
                    </p>
                    <h4 className="mt-2 text-lg font-bold text-navy-700 dark:text-white">
                      {kpi.loading || commercialSummary == null ? "…" : card.value}
                    </h4>
                    <p className="mt-2 text-[11px] text-gray-600 dark:text-gray-300">
                      {card.hint}
                    </p>
                  </div>
                  <div className="p-3 rounded-full shadow-sm bg-white/80 text-brand-500 dark:bg-navy-800/80 dark:text-white">
                    {card.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card id="seccion-folios" extra="mt-6 rounded-[20px] border border-gray-200/70 p-4 dark:border-white/10 sm:p-5">
        <div className="flex flex-col gap-2 mb-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-xs font-bold tracking-wide uppercase text-navy-700 dark:text-white">
              Folios disponibles
            </h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Secuencias en estado <span className="font-medium">available</span>{" "}
              u{" "}
              <span className="font-medium">pending_activation</span>
              {" "}
              · Normal desde <span className="font-medium">5</span> folios;
              alerta si hay menos; sin folios es error.
              {folioRows.loading ? (
                ""
              ) : (
                <>
                  {" "}
                  · Total disponibles (suma):{" "}
                  {formatInt(
                    folioDisponiblesItems.reduce(
                      (s, r) => s + Number(r.folios_disponibles ?? 0),
                      0,
                    ),
                  )}
                </>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            <Link
              to="/admin/caf"
              className="inline-flex items-center gap-0.5 rounded-md bg-navy-700 px-2 py-1 text-[10px] font-semibold text-white shadow-sm transition hover:bg-navy-800 dark:bg-brand-500 dark:hover:bg-brand-600"
            >
              <MdUpload className="w-3 h-3" />
              Subir CAF
            </Link>
            <button
              type="button"
              onClick={refreshFolios}
              disabled={folioRows.loading}
              className="inline-flex items-center gap-0.5 rounded-md border border-gray-300 bg-white px-2 py-1 text-[10px] font-semibold text-navy-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-60 dark:border-white/15 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
            >
              <MdRefresh
                className={`h-3 w-3 ${folioRows.loading ? "animate-spin" : ""}`}
              />
              Actualizar
            </button>
          </div>
        </div>

        {folioRows.loading ? (
          <p className="text-[10px] text-gray-500 dark:text-gray-400">Cargando folios…</p>
        ) : folioDisponiblesItems.length === 0 ? (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            No hay secuencias disponibles.{" "}
            <Link
              to="/admin/caf"
              className="font-medium text-brand-500 dark:text-brand-300"
            >
              Cargá CAF
            </Link>{" "}
            o activá una secuencia.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 sm:gap-2 lg:grid-cols-4">
            {folioDisponiblesItems.map((row) => {
              const n = Number(row.folios_disponibles ?? 0);
              const tipo = row.tipo_dte ?? "—";
              const titulo = labelTipoDte(tipo);
              const subtitulo = [
                labelAmbienteSii(row.ambiente),
                row.rut_emisor,
              ]
                .filter(Boolean)
                .join(" · ");
              const esError = n <= 0;
              const esAlerta =
                !esError && n < FOLIOS_ALERTA_UMBRAL;
              const barra = esError
                ? "bg-red-500"
                : esAlerta
                  ? "bg-amber-400 dark:bg-amber-500"
                  : "bg-emerald-500";
              const fondoCard = esError
                ? "bg-red-50/90 dark:bg-red-950/70 dark:shadow-[inset_0_0_0_1px_rgba(248,113,113,0.35)]"
                : esAlerta
                  ? "bg-amber-50/95 dark:bg-[#3d2a14] dark:shadow-[inset_0_0_0_1px_rgba(251,191,36,0.45)]"
                  : "bg-white dark:bg-navy-800/80";
              const bordeCard = esError
                ? "border-red-200/90 dark:border-red-500/45"
                : esAlerta
                  ? "border-amber-200/90 dark:border-amber-400/50"
                  : "border-gray-200 dark:border-white/10";

              return (
                <div
                  key={row.id}
                  className={`flex overflow-hidden rounded-lg border shadow-sm ${bordeCard}`}
                >
                  <div className={`w-0.5 shrink-0 sm:w-1 ${barra}`} aria-hidden />
                  <div
                    className={`flex min-w-0 flex-1 flex-col gap-1 p-1.5 sm:p-2 ${fondoCard}`}
                  >
                    <div className="flex gap-0.5 justify-between items-start">
                      <div className="flex gap-1 min-w-0">
                        <div className="flex justify-center items-center mt-px w-5 h-5 bg-gray-100 rounded-full shrink-0 dark:bg-white/10 sm:h-6 sm:w-6">
                          <MdDescription
                            className={`h-2.5 w-2.5 sm:h-3 sm:w-3 ${
                              esAlerta
                                ? "text-amber-800 dark:text-amber-200"
                                : esError
                                  ? "text-red-700 dark:text-red-200"
                                  : "text-navy-600 dark:text-white"
                            }`}
                          />
                        </div>
                        <div className="min-w-0">
                          <p
                            className={`text-[9px] font-bold leading-tight sm:text-[10px] ${
                              esAlerta
                                ? "text-navy-900 dark:text-amber-50"
                                : esError
                                  ? "text-navy-900 dark:text-red-50"
                                  : "text-navy-700 dark:text-white"
                            }`}
                          >
                            {titulo} · DTE {tipo}
                          </p>
                          <p
                            className={`line-clamp-1 text-[8px] sm:text-[9px] ${
                              esAlerta
                                ? "text-amber-900/90 dark:text-amber-200/95"
                                : esError
                                  ? "text-red-900/90 dark:text-red-200/90"
                                  : "text-gray-500 dark:text-gray-400"
                            }`}
                          >
                            {subtitulo || "—"}
                          </p>
                        </div>
                      </div>
                      {esAlerta ? (
                        <span className="inline-flex max-w-[4.5rem] shrink-0 items-center gap-0.5 rounded border border-amber-300/80 bg-amber-100 px-0.5 py-px text-[7px] font-bold uppercase leading-tight text-amber-950 dark:border-amber-400/60 dark:bg-amber-500/25 dark:text-amber-100 dark:shadow-sm sm:max-w-none sm:px-1 sm:text-[8px]">
                          <MdOutlineWarningAmber className="w-2 h-2 text-amber-700 shrink-0 dark:text-amber-300 sm:h-2.5 sm:w-2.5" />
                          Pocos folios
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-auto">
                      <div className="flex gap-0.5 items-baseline">
                        <span
                          className={`text-base font-bold tabular-nums leading-none sm:text-lg ${
                            esError
                              ? "text-red-600 dark:text-red-400"
                              : esAlerta
                                ? "text-amber-900 dark:text-amber-200"
                                : "text-navy-800 dark:text-white"
                          }`}
                        >
                          {formatInt(n)}
                        </span>
                        <span
                          className={`text-[8px] sm:text-[9px] ${
                            esAlerta
                              ? "text-amber-800/90 dark:text-amber-200/90"
                              : esError
                                ? "text-red-800/80 dark:text-red-200/85"
                                : "text-gray-500 dark:text-gray-400"
                          }`}
                        >
                          disponibles
                        </span>
                      </div>
                      {esAlerta ? (
                        <p className="mt-0.5 text-[8px] leading-tight text-amber-950 dark:text-amber-100 sm:text-[9px]">
                          {folioPieStock(n)}
                        </p>
                      ) : null}
                      {esError ? (
                        <p className="mt-0.5 text-[8px] leading-tight text-red-700 dark:text-red-300 sm:text-[9px]">
                          {folioPieStock(n)}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="mt-6">
        <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Salud de emisión
        </p>
      </div>
      <DteEmissionStatsChart
        desde={dashboardDesde}
        hasta={dashboardHasta}
        onDesdeChange={setDashboardDesde}
        onHastaChange={setDashboardHasta}
        hideControls
        onSummaryChange={setCommercialSummary}
      />

      <div className="mt-6">
        <HourlySalesHeatmap
          desde={dashboardDesde}
          hasta={dashboardHasta}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 mt-6 xl:grid-cols-3">
        <Card id="seccion-accesos" extra="rounded-[20px] border border-gray-200/70 p-6 dark:border-white/10">
          <h3 className="mb-4 text-lg font-bold text-navy-700 dark:text-white">
            Accesos rápidos
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Link
              to="/admin/data-tables"
              className="flex gap-3 items-center px-4 py-3 text-sm font-medium rounded-xl border border-gray-200 transition bg-lightPrimary text-navy-700 hover:border-brand-500/40 hover:bg-white dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
            >
              <MdDescription className="w-5 h-5 shrink-0 text-brand-500 dark:text-brand-300" />
              DTE emitidos
            </Link>
            <Link
              to="/admin/dte-compras"
              className="flex gap-3 items-center px-4 py-3 text-sm font-medium rounded-xl border border-gray-200 transition bg-lightPrimary text-navy-700 hover:border-brand-500/40 hover:bg-white dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
            >
              <MdOutlineReceiptLong className="w-5 h-5 shrink-0 text-brand-500 dark:text-brand-300" />
              DTE compras (bandeja)
            </Link>
            <Link
              to="/admin/caf"
              className="flex gap-3 items-center px-4 py-3 text-sm font-medium rounded-xl border border-gray-200 transition bg-lightPrimary text-navy-700 hover:border-brand-500/40 hover:bg-white dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
            >
              <MdInventory2 className="w-5 h-5 shrink-0 text-brand-500 dark:text-brand-300" />
              Folios & CAF
            </Link>
            <Link
              to="/admin/profile"
              className="flex gap-3 items-center px-4 py-3 text-sm font-medium rounded-xl border border-gray-200 transition bg-lightPrimary text-navy-700 hover:border-brand-500/40 hover:bg-white dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
            >
              <MdSettings className="w-5 h-5 shrink-0 text-brand-500 dark:text-brand-300" />
              Perfil de cuenta
            </Link>
          </div>
          <a
            href={SII_PORTAL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex gap-1.5 items-center mt-4 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-brand-300"
          >
            Portal SII (servicios en línea)
            <MdOpenInNew className="w-4 h-4" />
          </a>
        </Card>

        <TopProductsTable
          loading={topProducts.loading}
          items={topProducts.items}
          desde={dashboardDesde}
          hasta={dashboardHasta}
          sortBy={topProductsSortBy}
          onSortByChange={setTopProductsSortBy}
        />

        <Card id="seccion-ultimos" extra="rounded-[20px] border border-gray-200/70 p-6 dark:border-white/10 xl:col-span-3">
          <div className="flex gap-2 justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              Últimos DTE registrados
            </h3>
            <Link
              to="/admin/data-tables"
              className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-brand-300"
            >
              Ver todos
            </Link>
          </div>
          {recentDte.loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Cargando…</p>
          ) : recentDte.items.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Aún no hay documentos. Emití o sincronizá desde tus integraciones.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="text-xs text-gray-500 uppercase border-b border-gray-200 dark:border-white/10 dark:text-gray-400">
                    <th className="pr-3 pb-2 font-medium">Fecha</th>
                    <th className="pr-3 pb-2 font-medium">Tipo</th>
                    <th className="pr-3 pb-2 font-medium">Folio</th>
                    <th className="pb-2 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {recentDte.items.map((row) => (
                    <tr
                      key={row.id ?? row.document_uuid}
                      className="border-b border-gray-100 dark:border-white/5"
                    >
                      <td className="py-2.5 pr-3 text-gray-700 dark:text-gray-200">
                        {formatDateShort(row.created_at)}
                      </td>
                      <td className="py-2.5 pr-3 text-navy-700 dark:text-white">
                        {row.tipo_dte ?? "—"}
                      </td>
                      <td className="py-2.5 pr-3 text-gray-800 dark:text-gray-100">
                        {row.folio ?? "—"}
                      </td>
                      <td className="py-2.5 text-gray-600 dark:text-gray-300">
                        {row.lifecycle_status ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div id="seccion-rechazos" className="mt-6">
        <Card extra="rounded-[20px] border border-gray-200/70 p-6 dark:border-white/10">
          <div className="flex gap-2 justify-between items-center mb-4">
            <div className="flex gap-2 items-center">
              <MdOutlineWarningAmber className="w-5 h-5 text-amber-500 dark:text-amber-300" />
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                DTE con rechazo o reparo
              </h3>
            </div>
            <Link
              to="/admin/data-tables?lifecycle_status=rejected"
              className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-brand-300"
            >
              Ver rechazados
            </Link>
          </div>
          {criticalDte.loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Cargando…</p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-2 mb-3 sm:grid-cols-3">
                <Link
                  to="/admin/data-tables?lifecycle_status=rejected"
                  className="px-3 py-2 bg-red-50 rounded-lg border border-red-200 transition hover:bg-red-100/80 dark:border-red-500/40 dark:bg-red-500/10 dark:hover:bg-red-500/15"
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">Rechazados</p>
                  <p className="mt-0.5 text-lg font-bold tabular-nums text-red-800 dark:text-red-100">
                    {formatInt(criticalDte.summary.rejected || 0)}
                  </p>
                </Link>
                <Link
                  to="/admin/data-tables?lifecycle_status=error"
                  className="px-3 py-2 bg-orange-50 rounded-lg border border-orange-200 transition hover:bg-orange-100/80 dark:border-orange-500/40 dark:bg-orange-500/10 dark:hover:bg-orange-500/15"
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-orange-700 dark:text-orange-300">Con error</p>
                  <p className="mt-0.5 text-lg font-bold tabular-nums text-orange-800 dark:text-orange-100">
                    {formatInt(criticalDte.summary.error || 0)}
                  </p>
                </Link>
                <Link
                  to="/admin/data-tables?q=reparo"
                  className="px-3 py-2 bg-amber-50 rounded-lg border border-amber-200 transition hover:bg-amber-100/80 dark:border-amber-500/40 dark:bg-amber-500/10 dark:hover:bg-amber-500/15"
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Con reparo SII</p>
                  <p className="mt-0.5 text-lg font-bold tabular-nums text-amber-800 dark:text-amber-100">
                    {formatInt(criticalDte.summary.reparo || 0)}
                  </p>
                </Link>
              </div>
              <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
                Se muestran solo los <span className="font-semibold">3 incidentes más recientes</span>. Haz clic para abrir la bandeja filtrada.
              </p>
              {criticalDte.items.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No hay documentos críticos recientes (rechazados/reparos).
                </p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-white/10">
                  {criticalDte.items.map((row) => (
                    <li key={row.id ?? row.document_uuid} className="py-3 first:pt-0 last:pb-0">
                      <Link
                        to={buildCriticalDteFilterLink(row)}
                        className="flex flex-col gap-0.5 px-1 py-1 rounded-lg transition group hover:bg-amber-50/60 dark:hover:bg-amber-500/10"
                      >
                        <span className="text-sm font-medium text-navy-700 group-hover:text-amber-800 dark:text-white dark:group-hover:text-amber-200">
                          DTE {row.tipo_dte ?? "—"} · Folio {row.folio ?? "—"}
                        </span>
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {row.rut_receptor || row.receptor_razon_social || "Sin receptor"} ·{" "}
                          {formatDateShort(row.created_at)}
                        </span>
                        <span className="text-xs text-amber-700 dark:text-amber-300">
                          Estado ciclo: {row.lifecycle_status || "—"} · SII: {row.sii_estado || row.sii_glosa || "—"}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </Card>
      </div>

      {/* A quién le compramos más, en el rango del filtro general. El monto es
          NETO: las notas de crédito restan, así que un proveedor al que se le
          anuló medio pedido no aparece arriba por el bruto. */}
      <div className="mt-6">
        <Card extra="rounded-[20px] border border-gray-200/70 p-6 dark:border-white/10">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              Proveedores con más compras
            </h3>
            <Link
              to="/admin/data-tables"
              className="text-xs font-semibold text-brand-500 hover:underline dark:text-brand-300"
            >
              Ver compras
            </Link>
          </div>

          {topProveedores.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No hay compras en el rango de fechas seleccionado.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th className="pb-2 pr-3 font-semibold">Proveedor</th>
                    <th className="pb-2 pr-3 font-semibold">RUT</th>
                    <th className="pb-2 pr-3 text-right font-semibold">Docs.</th>
                    <th className="pb-2 text-right font-semibold">Total comprado</th>
                  </tr>
                </thead>
                <tbody>
                  {topProveedores.map((p) => (
                    <tr
                      key={p.rut_emisor}
                      className="border-t border-gray-100 dark:border-white/10"
                    >
                      <td className="py-2 pr-3 text-navy-700 dark:text-white">
                        <span className="block max-w-[260px] truncate" title={p.razon_social || ""}>
                          {p.razon_social || "—"}
                        </span>
                        {p.notas_credito > 0 ? (
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {p.notas_credito} nota(s) de crédito descontada(s)
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{p.rut_emisor}</td>
                      <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">
                        {p.documentos}
                      </td>
                      <td className="py-2 text-right font-semibold text-navy-700 dark:text-white">
                        {formatMoney(p.monto_neto)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div id="seccion-compras" className="mt-6">
        <Card extra="rounded-[20px] border border-gray-200/70 p-6 dark:border-white/10">
          <div className="flex flex-wrap gap-2 justify-between items-center mb-4">
            <div className="flex gap-2 items-center">
              <MdOutlineEmail className="w-5 h-5 text-brand-500 dark:text-brand-300" />
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                Compras por correo (recientes)
              </h3>
            </div>
            <Link
              to="/admin/dte-compras"
              className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-brand-300"
            >
              Abrir bandeja
            </Link>
          </div>
          {inboxPreview.loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Cargando…</p>
          ) : inboxPreview.items.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No hay mensajes recientes en la bandeja Gmail DTE.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-white/10">
              {inboxPreview.items.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-col gap-0.5 py-3 text-sm first:pt-0 last:pb-0"
                >
                  <span className="font-medium text-navy-700 dark:text-white">
                    {row.subject?.trim() || "(Sin asunto)"}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {row.from_email || "—"} ·{" "}
                    {formatDateShort(row.received_at || row.created_at)}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Estado: {row.processing_status || "—"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
